import { Table, TableBody, TableCell, TableContainer, TableHead, TableHeader, TableRow } from '@carbon/react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { StatusTag } from '../components/StatusTag';
import { ordersKey, useFormat, useOrderSetup, useOrdersApi, type OrdersMode } from './model';
import type { Payment } from './ordersApi';
import { Facts, Timeline, TransactionFrame } from './TransactionDetail';
import { StoreName } from './TransactionFilters';
import './orders.scss';

/** One payment or refund: what it was, the refunds against it, and its history (ADR 0011). */
export function PaymentHistoryPage({ mode }: { mode: OrdersMode }) {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const api = useOrdersApi();
  const format = useFormat();
  const { paymentMethodName, reasonName } = useOrderSetup();
  const detail = useQuery({ queryKey: [...ordersKey, 'payment', id], queryFn: () => api.payment(id) });
  const data = detail.data;
  const p = data?.payment;
  const money = (value: number | string | null | undefined, payment: Payment) => format.money(value, payment.currencyCode);
  const typeLabel = (type: string) => t(`orders.methodTypes.${type}` as I18nKey, { defaultValue: type });

  return (
    <TransactionFrame error={detail.error} loading={!data} backTo="/sales/payments">
      {data && p && (
        <>
          <PageHeader
            title={t(p.kind === 'refund' ? 'transactions.detail.refundTitle' : 'transactions.detail.paymentTitle', {
              reference: p.providerReference ?? data.orderNumber ?? '',
            })}
            tag={
              <StatusTag status={p.status}>
                {t(`transactions.payments.statuses.${p.status}` as I18nKey, { defaultValue: p.status })}
              </StatusTag>
            }
          />
          <Facts
            facts={[
              [t('transactions.detail.facts.order'), <Link key="order" to={`/orders/${p.orderId}`}>{data.orderNumber ?? p.orderId}</Link>],
              [t('transactions.detail.facts.store'), mode === 'ho' ? <StoreName key="store" id={p.storeId} /> : null],
              [t('transactions.detail.facts.kind'), t(`orders.payments.kinds.${p.kind}` as I18nKey, { defaultValue: p.kind })],
              [t('transactions.detail.facts.amount'), <span key="amount" className="pos-admin-page__number">{money(p.amount, p)}</span>],
              [t('transactions.detail.facts.provider'), p.providerName],
              [t('transactions.detail.facts.method'), paymentMethodName(p.paymentMethodId) ?? typeLabel(p.methodType)],
              [t('transactions.detail.facts.reference'), p.providerReference],
              [t('transactions.detail.facts.qrReference'), p.qrReference],
              [t('transactions.detail.facts.card'), p.cardLast4 ? `${p.cardBrand ?? ''} •••• ${p.cardLast4}`.trim() : null],
              [t('transactions.detail.facts.tendered'), p.tenderedAmount != null ? money(p.tenderedAmount, p) : null],
              [t('transactions.detail.facts.change'), Number(p.changeAmount) ? money(p.changeAmount, p) : null],
              [t('transactions.detail.facts.confirmation'), t(`transactions.payments.confirmation.${p.confirmationMode}` as I18nKey, { defaultValue: p.confirmationMode })],
              [t('transactions.detail.facts.receivedBy'), p.receivedByName],
              [t('transactions.detail.facts.paidAt'), format.dateTime(p.paidAt)],
              [t('orders.refund.reason'), p.reasonCodeId ? reasonName(p.reasonCodeId) : null],
              [t('transactions.detail.facts.refundable'), p.kind === 'payment' ? money(p.refundable, p) : null],
              [
                t('transactions.detail.facts.refundOf'),
                data.refundOf ? (
                  <Link key="refund-of" to={`/sales/payments/${data.refundOf.id}`}>
                    {data.refundOf.providerReference ?? money(data.refundOf.amount, data.refundOf)}
                  </Link>
                ) : null,
              ],
            ]}
          />

          <h2 className="pos-orders__section-title">{t('transactions.detail.timeline')}</h2>
          <Timeline
            format={format.dateTime}
            entries={data.events.map((e) => ({
              id: e.id,
              at: e.occurredAt,
              title: t(`transactions.detail.events.${e.eventType}` as I18nKey, { defaultValue: e.eventType }),
              detail: [e.amount != null ? money(e.amount, p) : '', e.note ?? ''].filter(Boolean).join(' · '),
              by: e.userName,
            }))}
          />

          {p.kind === 'payment' && (
            <>
              <h2 className="pos-orders__section-title">{t('transactions.detail.refunds')}</h2>
              {data.refunds.length === 0 ? (
                <EmptyState headingLevel={3} heading={t('transactions.detail.noRefunds')} />
              ) : (
                <TableContainer>
                <Table aria-label={t('transactions.detail.refunds')}>
                  <TableHead>
                    <TableRow>
                      <TableHeader>{t('transactions.detail.facts.paidAt')}</TableHeader>
                      <TableHeader>{t('transactions.detail.facts.amount')}</TableHeader>
                      <TableHeader>{t('orders.refund.reason')}</TableHeader>
                      <TableHeader>{t('transactions.detail.facts.receivedBy')}</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.refunds.map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>
                          <Link to={`/sales/payments/${r.id}`}>{format.dateTime(r.paidAt)}</Link>
                        </TableCell>
                        <TableCell className="pos-admin-page__number">−{money(r.amount, r)}</TableCell>
                        <TableCell>{reasonName(r.reasonCodeId) ?? ''}</TableCell>
                        <TableCell>{r.receivedByName ?? ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </TableContainer>
              )}
            </>
          )}
        </>
      )}
    </TransactionFrame>
  );
}
